import {
  createElement,
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { OrgChannelsProvider } from "../components/blackboard/OrgChannelsProvider";
import {
  getAuthSessionAsPublic,
  logoutAsPublic,
  requestOtpCodeAsPublic,
  verifyOtpCodeAsPublic,
} from "../dashboard-client";
import { isAdmin } from "../utils/rbac";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthState {
  status: AuthStatus;
  email: string | null;
  userId: string | null;
  organizationId: string | null;
  orgRole: string | null;
  globalRole: string | null;
}

interface DashboardAuthValue {
  email: string | null;
  finishOtp: (
    email: string,
    code: string,
    rememberDevice: boolean,
  ) => Promise<void>;
  globalRole: string | null;
  logout: () => Promise<void>;
  markUnauthorized: () => void;
  orgRole: string | null;
  organizationId: string | null;
  refreshSession: () => Promise<void>;
  startOtp: (email: string) => Promise<void>;
  status: AuthStatus;
  userId: string | null;
}

const EMPTY_STATE: AuthState = {
  email: null,
  globalRole: null,
  orgRole: null,
  organizationId: null,
  status: "unauthenticated",
  userId: null,
};

const DashboardAuthContext = createContext<DashboardAuthValue | null>(null);

function useDashboardAuthValue(): DashboardAuthValue {
  const [state, setState] = useState<AuthState>({
    ...EMPTY_STATE,
    status: "loading",
  });

  const loadSession = useCallback(async () => {
    try {
      const { data } = await getAuthSessionAsPublic();
      if (data.authenticated) {
        setState({
          email: data.email ?? data.user?.email ?? null,
          globalRole: data.global_role ?? null,
          orgRole: data.org_role ?? null,
          organizationId: data.organization_id ?? null,
          status: "authenticated",
          userId: data.user_id ?? null,
        });
      } else {
        setState(EMPTY_STATE);
      }
    } catch {
      setState(EMPTY_STATE);
    }
  }, []);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  const startOtp = useCallback(async (email: string) => {
    await requestOtpCodeAsPublic({ body: { email } });
  }, []);

  const finishOtp = useCallback(
    async (email: string, code: string, rememberDevice: boolean) => {
      await verifyOtpCodeAsPublic({ body: { code, email, rememberDevice } });
      await loadSession();
    },
    [loadSession],
  );

  const logout = useCallback(async () => {
    await logoutAsPublic();
    setState(EMPTY_STATE);
  }, []);

  const markUnauthorized = useCallback(() => {
    setState(EMPTY_STATE);
  }, []);

  return useMemo(
    () => ({
      email: state.email,
      finishOtp,
      globalRole: state.globalRole,
      logout,
      markUnauthorized,
      orgRole: state.orgRole,
      organizationId: state.organizationId,
      refreshSession: loadSession,
      startOtp,
      status: state.status,
      userId: state.userId,
    }),
    [state, startOtp, finishOtp, logout, loadSession, markUnauthorized],
  );
}

export const DashboardAuthProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const value = useDashboardAuthValue();

  return createElement(
    DashboardAuthContext.Provider,
    { value },
    createElement(
      OrgChannelsProvider,
      {
        includeDeleted: isAdmin(value.orgRole, value.globalRole),
        organizationId:
          value.status === "authenticated" ? value.organizationId : null,
        userId: value.status === "authenticated" ? value.userId : null,
      },
      children,
    ),
  );
};

export function useDashboardAuth(): DashboardAuthValue {
  const context = useContext(DashboardAuthContext);

  if (context === null) {
    throw new Error(
      "useDashboardAuth must be used within DashboardAuthProvider.",
    );
  }

  return context;
}

/** Provider-optional variant for components that also render in bare test
 * trees; absent a provider it reports null and callers fall back to the
 * least-privileged view (the server enforces the real boundary). */
export function useOptionalDashboardAuth(): DashboardAuthValue | null {
  return useContext(DashboardAuthContext);
}

/** Environment-admin predicate, mirroring the server's rbac.ts: platform
 * admin, or org role at least admin (owner > admin > member). Gates the
 * admin-only environment lifecycle surfaces (archive / kill / remove / GC). */
export function hasEnvironmentAdminAccess(auth: {
  globalRole: string | null;
  orgRole: string | null;
}): boolean {
  return (
    auth.globalRole === "admin" ||
    auth.orgRole === "admin" ||
    auth.orgRole === "owner"
  );
}
